import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Card, Btn, Input, Select, Textarea } from "../../components/UI";
import { toast } from "sonner";
import {
  Upload, Play, Pause, Mic, Wand2, LayoutGrid, Video, Download,
  CheckCircle2, Circle, Loader2, AlertTriangle, PackageCheck, RefreshCw, Sparkles, Rocket,
  Languages, ShieldCheck, BarChart3,
} from "lucide-react";

const PARTY_PRESETS = {
  Democrat: { primary: "#1D4ED8", secondary: "#0B1B3F", text: "#FFFFFF" },
  Republican: { primary: "#B91C1C", secondary: "#250808", text: "#FFFFFF" },
  Independent: { primary: "#0E7490", secondary: "#1a1816", text: "#FFFFFF" },
};

const LANGUAGES = ["English", "Spanish", "French", "German", "Portuguese", "Hindi", "Chinese", "Arabic", "Korean", "Vietnamese", "Tagalog", "Polish", "Italian"];

const bust = (url, v) => (url ? `${url}${url.includes("?") ? "&" : "?"}v=${encodeURIComponent(v || "")}` : url);

/* ---------------- Step 1: Setup ---------------- */
export const StepSetup = ({ project, save, saving }) => {
  const [f, setF] = useState({
    name: project.name, candidate_name: project.candidate_name, committee_name: project.committee_name,
    party: project.party, headline: project.headline, cta: project.cta, landing_url: project.landing_url,
    language: project.language, brand_colors: project.brand_colors || PARTY_PRESETS.Independent,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="step-setup">
      <Card className="lg:col-span-2 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Project name" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="setup-name" />
          <Input label="Candidate name" value={f.candidate_name} onChange={(e) => set("candidate_name", e.target.value)} data-testid="setup-candidate" />
          <Input label="Committee (disclaimer source)" value={f.committee_name} onChange={(e) => set("committee_name", e.target.value)} data-testid="setup-committee" />
          <Select label="Language" value={f.language} onChange={(e) => set("language", e.target.value)} data-testid="setup-language">
            {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
          </Select>
          <Input label="Banner headline" placeholder="A Stronger Tomorrow Starts Now" value={f.headline} onChange={(e) => set("headline", e.target.value)} data-testid="setup-headline" />
          <Input label="Call to action" placeholder="Learn More" value={f.cta} onChange={(e) => set("cta", e.target.value)} data-testid="setup-cta" />
        </div>
        <Input label="Landing page URL (UTM tags added automatically)" placeholder="https://voss.totalreach.us" value={f.landing_url} onChange={(e) => set("landing_url", e.target.value)} data-testid="setup-landing" />
        <div className="rounded-md border border-cyan-400/20 bg-cyan-400/5 px-3 py-2 text-xs text-cyan-200" data-testid="disclaimer-preview">
          Compliance disclaimer (auto-applied to all assets): <span className="font-semibold">Paid for by {f.committee_name || "[Committee Name]"}.</span>
        </div>
        <Btn onClick={() => save(f)} disabled={saving} data-testid="setup-save">{saving ? "Saving…" : "Save setup"}</Btn>
      </Card>
      <Card>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Brand colors</div>
        <div className="flex gap-2 mb-4">
          {Object.keys(PARTY_PRESETS).map((p) => (
            <button key={p} data-testid={`party-preset-${p.toLowerCase()}`}
              onClick={() => setF((prev) => ({ ...prev, party: p, brand_colors: { ...PARTY_PRESETS[p] } }))}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.party === p ? "border-cyan-400/40 text-cyan-300 bg-cyan-400/10" : "border-white/10 text-slate-400 hover:text-white"}`}>
              {p}
            </button>
          ))}
        </div>
        {["primary", "secondary"].map((k) => (
          <div key={k} className="flex items-center gap-3 mb-3">
            <input type="color" value={f.brand_colors[k]} data-testid={`color-${k}`}
              onChange={(e) => set("brand_colors", { ...f.brand_colors, [k]: e.target.value })}
              className="w-9 h-9 rounded cursor-pointer bg-transparent border border-white/10" />
            <div>
              <div className="text-xs capitalize text-slate-300">{k}</div>
              <div className="text-[10px] text-slate-500 uppercase">{f.brand_colors[k]}</div>
            </div>
          </div>
        ))}
        <div className="mt-4 rounded-md overflow-hidden border border-white/10" data-testid="color-preview">
          <div className="h-14 flex items-center px-3 text-sm font-semibold" style={{ background: f.brand_colors.secondary, color: f.brand_colors.text }}>
            {f.headline || "Your headline"}
          </div>
          <div className="h-9 flex items-center px-3" style={{ background: f.brand_colors.primary }}>
            <span className="text-xs font-semibold text-white">{f.cta || "Learn More"}</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

/* ---------------- Step 2: Media ---------------- */
const UploadBox = ({ label, hint, accept, current, uploading, onFile, testid }) => {
  const ref = useRef();
  return (
    <Card>
      <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">{label}</div>
      <p className="text-xs text-slate-500 mb-3">{hint}</p>
      {current?.url && (accept.startsWith("image") ? (
        <img src={current.url} alt="" className="w-full h-44 object-cover rounded-md border border-white/10 mb-3" data-testid={`${testid}-preview`} />
      ) : (
        <video src={current.url} controls className="w-full h-44 rounded-md border border-white/10 mb-3 bg-black" data-testid={`${testid}-preview`} />
      ))}
      <input ref={ref} type="file" accept={accept} className="hidden" data-testid={`${testid}-input`}
        onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
      <Btn variant="secondary" onClick={() => ref.current?.click()} disabled={uploading} data-testid={`${testid}-btn`}>
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {current ? "Replace" : "Upload"}
      </Btn>
      {current && <span className="ml-3 text-xs text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {current.filename}</span>}
    </Card>
  );
};

export const StepMedia = ({ project, refresh }) => {
  const [uploading, setUploading] = useState("");
  const upload = async (kind, file) => {
    setUploading(kind);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/ad-studio/projects/${project.id}/upload?kind=${kind}`, fd);
      toast.success(`${kind === "photo" ? "Photo" : "Video"} uploaded`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading("");
    }
  };
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="step-media">
      <UploadBox label="Candidate photo (required)" testid="photo-upload"
        hint="Clear, front-facing headshot (JPG/PNG, max 10MB). Used for the HeyGen talking-photo video and all banners."
        accept="image/jpeg,image/png" current={project.assets?.photo} uploading={uploading === "photo"}
        onFile={(f) => upload("photo", f)} />
      <UploadBox label="Candidate video (optional)" testid="video-upload"
        hint="Existing footage of the candidate speaking (MP4/MOV, max 60MB). Enables lip-sync mode: HeyGen re-syncs the mouth to your new AI voice track."
        accept="video/mp4,video/quicktime,video/webm" current={project.assets?.video} uploading={uploading === "video"}
        onFile={(f) => upload("video", f)} />
    </div>
  );
};

/* ---------------- Step 3: Script & Voice ---------------- */
const words = (t) => (t || "").trim().split(/\s+/).filter(Boolean).length;

export const StepVoice = ({ project, save, saving, refresh }) => {
  const [s15, setS15] = useState(project.script_15 || "");
  const [s30, setS30] = useState(project.script_30 || "");
  const [voices, setVoices] = useState(null);
  const [selected, setSelected] = useState(project.voice_id || "");
  const [search, setSearch] = useState("");
  const [playing, setPlaying] = useState("");
  const [cloneName, setCloneName] = useState(project.candidate_name ? `${project.candidate_name} (cloned)` : "");
  const [cloning, setCloning] = useState(false);
  const [issues, setIssues] = useState(project.key_issues || "");
  const [writing, setWriting] = useState(false);
  const [variants, setVariants] = useState(project.script_variants || []);
  const audioRef = useRef(null);
  const cloneFileRef = useRef();

  const writeAI = async () => {
    setWriting(true);
    try {
      const r = await api.post(`/ad-studio/projects/${project.id}/write-scripts`, { key_issues: issues });
      setVariants(r.data.variants || []);
      toast.success("3 variants drafted — pick one, edit, then save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI script writer failed");
    } finally {
      setWriting(false);
    }
  };

  const applyVariant = (v) => {
    setS15(v.script_15);
    setS30(v.script_30);
    toast.success(`Variant "${v.angle}" applied — remember to save`);
  };

  useEffect(() => {
    api.get("/ad-studio/voices").then((r) => setVoices(r.data.voices)).catch((e) => {
      toast.error(e?.response?.data?.detail || "Failed to load voices");
      setVoices([]);
    });
    return () => audioRef.current?.pause();
  }, []);

  const preview = (v) => {
    if (playing === v.voice_id) { audioRef.current?.pause(); setPlaying(""); return; }
    audioRef.current?.pause();
    if (!v.preview_url) return toast.info("No preview available for this voice");
    const a = new Audio(v.preview_url);
    audioRef.current = a;
    a.onended = () => setPlaying("");
    a.play();
    setPlaying(v.voice_id);
  };

  const persist = () => {
    const v = (voices || []).find((x) => x.voice_id === selected);
    save({ script_15: s15, script_30: s30, key_issues: issues, voice_id: selected, voice_name: v?.name || project.voice_name, voice_mode: v?.category === "cloned" ? "cloned" : "stock" });
  };

  const clone = async (file) => {
    if (!cloneName.trim()) return toast.error("Name the cloned voice first");
    setCloning(true);
    const fd = new FormData();
    fd.append("name", cloneName);
    fd.append("file", file);
    try {
      const r = await api.post(`/ad-studio/projects/${project.id}/voice-clone`, fd);
      toast.success("Voice cloned — selected for this project");
      setSelected(r.data.voice_id);
      const vr = await api.get("/ad-studio/voices");
      setVoices(vr.data.voices);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice clone failed");
    } finally {
      setCloning(false);
    }
  };

  const filtered = (voices || []).filter((v) =>
    !search || `${v.name} ${v.language} ${v.accent} ${v.gender}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="step-voice">
      <div className="space-y-4">
        <Card data-testid="ai-writer-card">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> AI Script Writer</div>
          <p className="text-xs text-slate-500 mb-3">Drafts both spots from the candidate, headline, CTA and your key issues. Review and edit before saving.</p>
          <Textarea label="Key issues / talking points" rows={2} value={issues} onChange={(e) => setIssues(e.target.value)}
            placeholder="lower costs, safer streets, protect local jobs" data-testid="key-issues-input" />
          <Btn className="mt-3" onClick={writeAI} disabled={writing} data-testid="ai-write-btn">
            {writing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {writing ? "Drafting 3 variants…" : "Write 3 A/B variants with AI"}
          </Btn>
          {variants.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="variants-list">
              {variants.map((v, i) => (
                <div key={i} className="rounded-md border border-white/10 bg-white/[0.03] p-3" data-testid={`variant-${i}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-cyan-300">
                      {String.fromCharCode(65 + i)} · {v.angle}
                    </span>
                    <button onClick={() => applyVariant(v)} data-testid={`use-variant-${i}`}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/10 transition-colors">
                      Use this variant
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">{v.script_15}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <Textarea label={`15-second script — ${words(s15)} words (~${Math.round(words(s15) / 2.4)}s spoken, aim ≤ 38 words)`}
            rows={4} value={s15} onChange={(e) => setS15(e.target.value)} data-testid="script-15"
            placeholder="I'm Jordan Voss. Our families deserve safer streets and lower costs. I'm running to deliver both." />
        </Card>
        <Card>
          <Textarea label={`30-second script — ${words(s30)} words (~${Math.round(words(s30) / 2.4)}s spoken, aim ≤ 78 words)`}
            rows={7} value={s30} onChange={(e) => setS30(e.target.value)} data-testid="script-30"
            placeholder="Longer version of your message with a proof point and a clear ask…" />
        </Card>
        <div className="rounded-md border border-amber-400/20 bg-amber-400/5 px-3 py-2 text-xs text-amber-200">
          "{project.disclaimer}" is automatically appended to the spoken audio if your script doesn't already include it.
        </div>
        <Btn onClick={persist} disabled={saving} data-testid="voice-save">{saving ? "Saving…" : "Save scripts & voice"}</Btn>
      </div>

      <div className="space-y-4">
        <Card padded={false} className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">ElevenLabs voice</div>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search voices…" data-testid="voice-search"
              className="bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs w-40 focus:outline-none focus:border-cyan-400/50" />
          </div>
          <div className="max-h-72 overflow-y-auto space-y-1 pr-1" data-testid="voice-list">
            {voices === null ? (
              <div className="text-xs text-slate-500 py-6 text-center">Loading voices…</div>
            ) : filtered.map((v) => (
              <div key={v.voice_id} data-testid={`voice-row-${v.voice_id}`}
                onClick={() => setSelected(v.voice_id)}
                className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer border transition-colors ${selected === v.voice_id ? "border-cyan-400/40 bg-cyan-400/10" : "border-transparent hover:bg-white/5"}`}>
                {selected === v.voice_id ? <CheckCircle2 className="w-4 h-4 text-cyan-300 shrink-0" /> : <Circle className="w-4 h-4 text-slate-600 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{v.name} {v.category === "cloned" && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-400/15 text-fuchsia-300 border border-fuchsia-400/20 ml-1">CLONED</span>}</div>
                  <div className="text-[10px] text-slate-500 truncate">{[v.language, v.accent, v.gender, v.age].filter(Boolean).join(" · ")}</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); preview(v); }} data-testid={`voice-preview-${v.voice_id}`}
                  className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:text-cyan-300 shrink-0">
                  {playing === v.voice_id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </button>
              </div>
            ))}
          </div>
        </Card>

        <Card data-testid="voice-clone-card">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1 flex items-center gap-2"><Mic className="w-3.5 h-3.5" /> Clone the candidate's voice</div>
          <p className="text-xs text-slate-500 mb-3">Upload ~60s of clean candidate audio (MP3/WAV, max 15MB). Instant voice clone via ElevenLabs — used for the voiceover and talking video.</p>
          <div className="flex items-end gap-3">
            <Input className="flex-1" label="Cloned voice name" value={cloneName} onChange={(e) => setCloneName(e.target.value)} data-testid="clone-name-input" />
            <input ref={cloneFileRef} type="file" accept="audio/*" className="hidden" data-testid="clone-file-input"
              onChange={(e) => e.target.files?.[0] && clone(e.target.files[0])} />
            <Btn variant="secondary" onClick={() => cloneFileRef.current?.click()} disabled={cloning} data-testid="clone-upload-btn">
              {cloning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {cloning ? "Cloning…" : "Upload sample & clone"}
            </Btn>
          </div>
        </Card>
      </div>
    </div>
  );
};

/* ---------------- Step 4: Generate ---------------- */
const StatusPill = ({ status }) => {
  const map = {
    completed: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
    failed: "text-rose-300 border-rose-400/30 bg-rose-400/10",
  };
  const cls = map[status] || "text-amber-300 border-amber-400/30 bg-amber-400/10";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>
      {!["completed", "failed"].includes(status) && <Loader2 className="w-3 h-3 animate-spin" />}
      {status}
    </span>
  );
};

const VideoSlot = ({ assets, k, label }) => {
  const v = assets[`video_${k}`];
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.03] p-3" data-testid={`video-slot-${k}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-slate-400">{label}</div>
        {v && <StatusPill status={v.url ? "completed" : v.status || "pending"} />}
      </div>
      {v?.url ? (
        <video controls className="w-full rounded bg-black" src={v.url} data-testid={`video-player-${k}`} />
      ) : v ? (
        <div className="text-xs text-slate-500 py-4 text-center">
          {v.error ? <span className="text-rose-300 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {v.error}</span> : "Rendering on HeyGen…"}
        </div>
      ) : (
        <div className="text-xs text-slate-600 py-4 text-center">Generate the voiceover first, then start the video render</div>
      )}
    </div>
  );
};

export const StepGenerate = ({ project, refresh }) => {
  const [busy, setBusy] = useState("");
  const [mode, setMode] = useState("photo");
  const assets = project.assets || {};

  const anyRendering = ["15", "30", "15_es", "30_es"].some((w) => {
    const v = assets[`video_${w}`];
    return v && (!["completed", "failed"].includes(v.status) || (v.status === "completed" && !v.url));
  });

  useEffect(() => {
    if (!anyRendering) return;
    const t = setInterval(async () => {
      try {
        await api.get(`/ad-studio/projects/${project.id}/video-status`);
        refresh();
      } catch { /* keep polling */ }
    }, 8000);
    return () => clearInterval(t);
  }, [anyRendering, project.id, refresh]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${key} failed`);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4" data-testid="step-generate">
      {/* Voiceover */}
      <Card data-testid="generate-voiceover-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display font-semibold flex items-center gap-2"><Mic className="w-4 h-4 text-cyan-300" /> AI Voiceover — ElevenLabs</div>
            <p className="text-xs text-slate-500 mt-0.5">Voice: <span className="text-slate-300">{project.voice_name || "not selected"}</span> · Language: {project.language} · Disclaimer auto-appended</p>
          </div>
          <Btn onClick={() => run("voiceover", () => api.post(`/ad-studio/projects/${project.id}/voiceover`, { which: "both" }), "Voiceovers generated")}
            disabled={busy === "voiceover"} data-testid="generate-voiceover-btn">
            {busy === "voiceover" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Generate 15s + 30s
          </Btn>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {["15", "30"].map((w) => (
            <div key={w} className="rounded-md border border-white/10 bg-white/[0.03] p-3">
              <div className="text-xs text-slate-400 mb-2">{w}s voiceover</div>
              {assets[`vo_${w}`] ? (
                <audio controls className="w-full h-9" src={bust(assets[`vo_${w}`].url, assets[`vo_${w}`].generated_at)} data-testid={`audio-player-${w}`} />
              ) : (
                <div className="text-xs text-slate-600 py-2">Not generated yet</div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Video */}
      <Card data-testid="generate-video-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="font-display font-semibold flex items-center gap-2"><Video className="w-4 h-4 text-cyan-300" /> Talking Candidate Video — HeyGen</div>
            <p className="text-xs text-slate-500 mt-0.5">Rendering takes 2–6 minutes. Status refreshes automatically every 8s.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="video-mode-select">
              <option value="photo">Photo avatar (from photo)</option>
              <option value="lipsync" disabled={!assets.video}>Lip-sync uploaded video{!assets.video ? " — upload video first" : ""}</option>
            </Select>
            <Btn onClick={() => run("video", () => api.post(`/ad-studio/projects/${project.id}/video`, { mode, which: "both" }), "HeyGen render started")}
              disabled={busy === "video" || anyRendering} data-testid="generate-video-btn">
              {busy === "video" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Generate videos
            </Btn>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {["15", "30"].map((w) => <VideoSlot key={w} assets={assets} k={w} label={`${w}s spot`} />)}
        </div>
      </Card>

      {/* Spanish Duo */}
      <Card data-testid="spanish-duo-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="font-display font-semibold flex items-center gap-2"><Languages className="w-4 h-4 text-cyan-300" /> Spanish Duo — Bilingual Ads</div>
            <p className="text-xs text-slate-500 mt-0.5">One click translates your saved English scripts into culturally adapted Spanish (Claude) and generates matching voiceovers with the same voice. "Pagado por…" disclaimer auto-appended.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Btn onClick={() => run("spanish-duo", () => api.post(`/ad-studio/projects/${project.id}/spanish-duo`, {}), "Spanish scripts + voiceovers ready")}
              disabled={busy === "spanish-duo"} data-testid="spanish-duo-btn">
              {busy === "spanish-duo" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />} Generate Spanish version
            </Btn>
            {(assets.vo_15_es && assets.vo_30_es) && (
              <Btn variant="secondary" onClick={() => run("es-video", () => api.post(`/ad-studio/projects/${project.id}/video`, { mode: "photo", which: "es_both" }), "Spanish video renders started")}
                disabled={busy === "es-video" || anyRendering} data-testid="spanish-video-btn">
                {busy === "es-video" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} Render Spanish videos
              </Btn>
            )}
          </div>
        </div>
        {project.script_15_es ? (
          <>
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              {["15", "30"].map((w) => (
                <div key={w} className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                  <div className="text-xs text-slate-400 mb-1.5">{w}s Spanish voiceover</div>
                  <p className="text-[11px] text-slate-500 italic mb-2 line-clamp-2">"{project[`script_${w}_es`]}"</p>
                  {assets[`vo_${w}_es`] ? (
                    <audio controls className="w-full h-9" src={bust(assets[`vo_${w}_es`].url, assets[`vo_${w}_es`].generated_at)} data-testid={`audio-player-${w}-es`} />
                  ) : (
                    <div className="text-xs text-slate-600 py-2">Not generated yet</div>
                  )}
                </div>
              ))}
            </div>
            {(assets.video_15_es || assets.video_30_es) && (
              <div className="grid md:grid-cols-2 gap-3">
                {["15_es", "30_es"].map((w) => <VideoSlot key={w} assets={assets} k={w} label={`${w.split("_")[0]}s Spanish spot`} />)}
              </div>
            )}
          </>
        ) : (
          <div className="text-xs text-slate-600 py-3 text-center">Save your English scripts and pick a voice first — then generate the Spanish version in one click</div>
        )}
      </Card>

      {/* Banners */}
      <Card data-testid="generate-banners-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display font-semibold flex items-center gap-2"><LayoutGrid className="w-4 h-4 text-cyan-300" /> IAB Banner Set</div>
            <p className="text-xs text-slate-500 mt-0.5">300×250 · 300×600 · 728×90 · 320×100 · 320×50 — photo, headline, CTA, brand colors and disclaimer baked in.</p>
          </div>
          <Btn onClick={() => run("banners", () => api.post(`/ad-studio/projects/${project.id}/banners`), "Banner set generated")}
            disabled={busy === "banners"} data-testid="generate-banners-btn">
            {busy === "banners" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Generate banners
          </Btn>
        </div>
        {(assets.banners || []).length > 0 ? (
          <div className="flex flex-wrap gap-4 items-end" data-testid="banner-grid">
            {assets.banners.map((b) => (
              <div key={b.size}>
                <img src={bust(b.url, assets.banners_generated_at)} alt={b.size}
                  className="border border-white/15 rounded max-w-full" style={{ maxHeight: 180 }} data-testid={`banner-${b.size}`} />
                <div className="text-[10px] text-slate-500 mt-1">{b.size}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-slate-600 py-4 text-center">No banners yet — needs a photo + headline from Setup</div>
        )}
      </Card>
    </div>
  );
};

/* ---------------- Step 5: Export ---------------- */
export const StepExport = ({ project, refresh }) => {
  const [building, setBuilding] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [target, setTarget] = useState("");
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const assets = project.assets || {};
  const review = project.review_status || "not_submitted";

  const submitReview = async () => {
    setSubmitting(true);
    try {
      await api.post(`/ad-studio/projects/${project.id}/submit-review`);
      toast.success("Submitted for compliance review");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data || [])).catch(() => {});
    api.get("/stackadapt/sync/mappings").then((r) => setMappings(r.data || [])).catch(() => {});
  }, []);

  const liveIds = new Set(mappings.filter((m) => m.sa_campaign_id).map((m) => m.local_campaign_id));

  const rows = [
    { ok: !!assets.video_15?.url, label: "15s talking video (720p master + 1080p CTV)" },
    { ok: !!assets.video_30?.url, label: "30s talking video (720p master + 1080p CTV)" },
    { ok: !!assets.vo_15, label: "15s voiceover (MP3)" },
    { ok: !!assets.vo_30, label: "30s voiceover (MP3)" },
    { ok: (assets.banners || []).length === 5, label: `IAB banner set (${(assets.banners || []).length}/5 sizes)` },
  ];

  const build = async () => {
    setBuilding(true);
    try {
      await api.post(`/ad-studio/projects/${project.id}/export`);
      toast.success("Ad package built — 1080p CTV masters included");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setBuilding(false);
    }
  };

  const push = async () => {
    setPushing(true);
    setPushResult(null);
    try {
      const r = await api.post(`/ad-studio/projects/${project.id}/push-to-campaign`, { campaign_id: target });
      setPushResult(r.data);
      if (r.data.synced.length) toast.success(`${r.data.synced.length} creative(s) live in the campaign`);
      else toast.error("Nothing was pushed — see details below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed");
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="step-export">
      <Card>
        <div className="font-display font-semibold mb-3 flex items-center gap-2"><PackageCheck className="w-4 h-4 text-cyan-300" /> Package contents</div>
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center gap-2 text-sm">
              {r.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <Circle className="w-4 h-4 text-slate-600" />}
              <span className={r.ok ? "text-slate-200" : "text-slate-500"}>{r.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 text-xs text-slate-500">
          Completed videos are auto-upscaled to <span className="text-slate-300">1920×1080 (8Mbps)</span> CTV masters at export. Plus <span className="text-slate-300">manifest.json</span> with UTM-tagged click URLs and trafficking notes, and a README. Partial packages are allowed.
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Btn onClick={build} disabled={building} data-testid="build-zip-btn">
            {building ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />} {building ? "Building (upscaling video)…" : "Build ZIP package"}
          </Btn>
          {assets.export && (
            <a href={assets.export.url} data-testid="download-zip-link"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/20 transition-colors">
              <Download className="w-4 h-4" /> Download {assets.export.filename}
            </a>
          )}
        </div>
        {assets.export && <div className="mt-2 text-[10px] text-slate-500">{assets.export.files} files · built {new Date(assets.export.created_at).toLocaleString()}</div>}
        <div className="mt-5 pt-4 border-t border-white/10">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2 flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5" /> Manual trafficking</div>
          <ol className="text-xs text-slate-500 space-y-1 list-decimal list-inside">
            <li>Upload each asset to your ad platform's creative library.</li>
            <li>Attach the <span className="text-slate-300">click_url</span> from manifest.json as its clickthrough URL (UTM pre-applied).</li>
            <li>Mark creatives as political and attach: <span className="text-cyan-300">{project.disclaimer}</span></li>
          </ol>
        </div>
      </Card>
      <Card data-testid="push-live-card">
        <div className="font-display font-semibold mb-1 flex items-center gap-2"><Rocket className="w-4 h-4 text-cyan-300" /> Push live to a campaign</div>
        <p className="text-xs text-slate-500 mb-3">
          Sends finished creatives straight into a live programmatic campaign — no download needed. Videos go to CTV/video campaigns (auto-upscaled to 1080p), banners go to display campaigns. UTM click URLs and political disclaimers attach automatically.
        </p>
        <div className="mb-4 rounded-md border border-white/10 bg-white/[0.03] p-3" data-testid="review-status-row">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldCheck className="w-4 h-4 text-cyan-300" />
            <span className="text-xs text-slate-400">Compliance approval:</span>
            <span data-testid="review-status-pill" className={`text-[10px] px-2 py-0.5 rounded-full border ${
              review === "approved" ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
              : review === "pending" ? "border-amber-400/30 bg-amber-400/10 text-amber-300"
              : review === "rejected" ? "border-rose-400/30 bg-rose-400/10 text-rose-300"
              : "border-white/10 bg-white/5 text-slate-400"}`}>
              {review === "not_submitted" || review === "" ? "Not submitted" : review}
            </span>
            {review !== "approved" && review !== "pending" && (
              <button onClick={submitReview} disabled={submitting} data-testid="submit-review-btn"
                className="text-[11px] px-2.5 py-1 rounded-full border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/10 transition-colors disabled:opacity-50">
                {submitting ? "Submitting…" : "Submit for compliance review"}
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            {review === "approved"
              ? "Approved — creatives can be pushed live."
              : review === "pending"
              ? "Awaiting review in Compliance Center → Ad Creatives. Pushing is locked until approved."
              : review === "rejected"
              ? "Rejected by compliance — revise assets and resubmit."
              : "Creatives must pass compliance review before they can be pushed live. Regenerating assets resets approval."}
          </p>
        </div>
        <Select label="Campaign" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="push-campaign-select">
          <option value="">— choose a campaign —</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id} disabled={!liveIds.has(c.id)}>
              {c.name}{liveIds.has(c.id) ? " · live" : " — not pushed live yet"}
            </option>
          ))}
        </Select>
        <Btn className="mt-3" onClick={push} disabled={!target || pushing || review !== "approved"} data-testid="push-live-btn">
          {pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} {pushing ? "Pushing creatives…" : "Push creatives live"}
        </Btn>
        {pushResult && (
          <div className="mt-4 space-y-1.5 text-xs" data-testid="push-result">
            {pushResult.synced.map((s) => (
              <div key={s.asset} className="flex items-center gap-1.5 text-emerald-300"><CheckCircle2 className="w-3.5 h-3.5" /> {s.asset} — live (ad #{s.sa_ad_id})</div>
            ))}
            {pushResult.failed.map((f) => (
              <div key={f.asset} className="flex items-center gap-1.5 text-rose-300"><AlertTriangle className="w-3.5 h-3.5" /> {f.asset}: {f.error}</div>
            ))}
            {pushResult.skipped.map((s) => (
              <div key={s.asset} className="text-slate-500">{s.asset} skipped — {s.reason}</div>
            ))}
          </div>
        )}
        <div className="mt-4 rounded-md border border-cyan-400/20 bg-cyan-400/5 px-3 py-2 text-xs text-cyan-200">
          Every asset carries the "Paid for by" disclosure — banners have it rendered on-image, audio/video have it spoken in the track.
        </div>
      </Card>
    </div>
  );
};

/* ---------------- Step 6: Performance ---------------- */
const fmtInt = (v) => (v == null ? "—" : Number(v).toLocaleString());
const fmtMoney = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtPct = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  return `${(n > 1 ? n : n * 100).toFixed(1)}%`;
};

export const StepPerformance = ({ project }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/ad-studio/projects/${project.id}/performance`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load performance");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="step-performance">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-cyan-300" /> Live Ad Performance</div>
          <p className="text-xs text-slate-500 mt-0.5">Real delivery for creatives pushed from this project — impressions, spend and video completion straight from the ad platform.</p>
        </div>
        <Btn variant="secondary" onClick={load} disabled={loading} data-testid="performance-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </Btn>
      </div>

      {data === null ? (
        <Card><div className="text-xs text-slate-500 py-8 text-center">Loading live delivery…</div></Card>
      ) : data.ads.length === 0 ? (
        <Card data-testid="performance-empty">
          <div className="py-10 text-center">
            <BarChart3 className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <div className="text-sm text-slate-300 font-medium">No creatives pushed yet</div>
            <p className="text-xs text-slate-500 mt-1">Get compliance approval in the Export step, push your creatives into a live campaign, and delivery stats will appear here.</p>
          </div>
        </Card>
      ) : (
        data.campaigns.map((c) => (
          <Card key={c.campaign_id} data-testid={`performance-campaign-${c.campaign_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <div className="font-display font-semibold">{c.campaign_name}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{c.channel || "—"} · Live #{c.sa_campaign_id}</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {c.ads.map((a) => (
                  <span key={a.id} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 bg-white/5 text-slate-400">
                    {a.asset} · ad #{a.sa_ad_id}
                  </span>
                ))}
              </div>
            </div>
            {c.error ? (
              <div className="text-xs text-rose-300 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> {c.error}</div>
            ) : c.processing || !c.stats ? (
              <div className="text-xs text-amber-300 flex items-center gap-1.5" data-testid="performance-processing">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Platform report is generating — newly pushed ads can take a while to log first impressions. Check back shortly.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3" data-testid="performance-stats">
                  <div className="rounded-md border border-cyan-400/20 bg-cyan-400/5 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Impressions</div>
                    <div className="font-display text-xl font-bold mt-1" data-testid="stat-impressions">{fmtInt(c.stats.impressionsBigint)}</div>
                  </div>
                  <div className="rounded-md border border-emerald-400/20 bg-emerald-400/5 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Spend</div>
                    <div className="font-display text-xl font-bold mt-1" data-testid="stat-spend">{fmtMoney(c.stats.cost)}</div>
                  </div>
                  <div className="rounded-md border border-violet-400/20 bg-violet-400/5 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Completion rate</div>
                    <div className="font-display text-xl font-bold mt-1" data-testid="stat-completion">{fmtPct(c.stats.videoCompletionRate)}</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="text-slate-500">Video completions <span className="text-slate-300 ml-1">{fmtInt(c.stats.videoCompletionsBigint)}</span></div>
                  <div className="text-slate-500">CTR <span className="text-slate-300 ml-1">{fmtPct(c.stats.ctr)}</span></div>
                  <div className="text-slate-500">eCPM <span className="text-slate-300 ml-1">{fmtMoney(c.stats.ecpm)}</span></div>
                  <div className="text-slate-500">Frequency <span className="text-slate-300 ml-1">{c.stats.frequency ?? "—"}</span></div>
                </div>
              </>
            )}
          </Card>
        ))
      )}
    </div>
  );
};
